'use client';

import { useState, useEffect, useRef } from 'react';
import { createClient } from '@/utils/supabase/client';
import { searchUsers } from '@/utils/supabase/queries';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';

interface User {
  username: string;
  avatar_url: string;
}

interface MentionAutocompleteProps {
  isOpen: boolean;
  query: string;
  onSelect: (username: string) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

export function MentionAutocomplete({
  isOpen,
  query,
  onSelect,
  onClose,
  position,
}: MentionAutocompleteProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  // Search for users when query changes
  useEffect(() => {
    if (!isOpen || query.length < 1) {
      setUsers([]);
      return;
    }

    const searchUsersQuery = async () => {
      try {
        const userData = await searchUsers(supabase, query, 8);
        setUsers(userData);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsersQuery, 200); // Debounce search
    return () => clearTimeout(debounceTimer);
  }, [isOpen, query, supabase]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || users.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % users.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + users.length) % users.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (users[selectedIndex]) {
            onSelect(users[selectedIndex].username);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, users, selectedIndex, onSelect, onClose]);

  if (!isOpen || (!loading && users.length === 0)) {
    return null;
  }

  return (
    <div
      ref={listRef}
      className="absolute z-50 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-48 overflow-y-auto min-w-[200px]"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {loading ? (
        <div className="px-3 py-2 text-zinc-400 text-sm">Searching...</div>
      ) : (
        users.map((user, index) => (
          <button
            key={user.username}
            className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-zinc-800 transition-colors ${
              index === selectedIndex ? 'bg-zinc-800' : ''
            }`}
            onClick={() => onSelect(user.username)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <ProfileAvatar
              username={user.username}
              avatarUrl={user.avatar_url}
              ownProfile={false}
              size={24}
            />
            <span className="text-sm text-zinc-100">@{user.username}</span>
          </button>
        ))
      )}
    </div>
  );
}
